import { useState, useEffect, useMemo, useContext } from "react";
import { useSelector } from "react-redux";
import orderBy from "lodash/orderBy";
import isEmpty from "lodash/isEmpty";

import {
  getUpdatedLineItemFromVariants,
  getCartProductType,
  generateLineItemsMappedData,
  groupItemsByShippedSeparately,
} from "@helpers/orders/cart";
import { isSwagProduct } from "@stadium/helpers/src/product/utils";
import { getProductAllVariantImages } from "@helpers/product-details";

import { InternalButton } from "@sharedComponents/buttons";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useCartListingContext } from "@contexts/cart-listing-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useCheckoutContext } from "@contexts/checkout-context";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";

import LineItemCard from "@sharedComponents/cart/line-item-card";

import usePopulateItems from "@hooks/cart/use-populate-items";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";
import useManipulateCartProduct from "@hooks/cart/use-manipulate-cart-product";
import useConfirmationModal from "@hooks/cart/use-confirmation-modal";

import { getCartOrderId, getCartProductZoneList } from "@selectors/cart";

import { ORDER_TYPES, SHIPPED_TOGETHER, SHIPPED_SEPARATELY } from "@constants/orders/cart";
import { ANALYTICS_EVENT_ACTION_CLEARED } from "@stadium/services/analytics/constants/default";
import { removeItemsConfirmationSendToOthers } from "@constants/confirmation-modal";

import styles from "../main.module.scss";

const emptyArr = [];
const VIEW_ALL_THRESHOLD = 2;

const CartProductItems = ({
  editable = true,
  productListData = {},
  variantsMapping = {},
  variantDataloading,
  getTotalPricePerRecipient,
}) => {
  const orderId = useSelector(getCartOrderId);
  const cartProductZoneList = useSelector(getCartProductZoneList);
  const { isSendToOthers } = useSendItemsContext();
  const withAnalytics = useWithAnalytics();

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const [isViewAllClicked, setIsViewAllClicked] = useState(false);

  const { sendToOthersZoneWiseRecipient, setSendToOthersZoneWiseRecipient } =
    useCartListingContext();

  const { orderCurrencyCode } = useCheckoutContext();

  const isProductFromSingleCountry = cartProductZoneList.length === 1;

  const getCartItemDetailInGuestFlow = (item) => {
    const variantImages = getProductAllVariantImages(item.product?.variantsIncludingMaster);

    const itemImages = isEmpty(item.images) ? variantImages : item.images;

    return {
      ...item,
      masterVariant: item.product?.masterVariant,
      images: isSendToOthers && isSwagProduct(item.intent) ? variantImages : itemImages,
    };
  };

  const updatedProductsWithVariant = (item) => {
    if (isGuestOrPreviewMode) {
      return getCartItemDetailInGuestFlow(item);
    }
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  const {
    lineItemsData = emptyArr,
    productSetsData = emptyArr,
    medleySetsData = emptyArr,
    zoneId = "",
  } = productListData;

  const mappedLineItems = useMemo(
    () =>
      generateLineItemsMappedData({
        lineItems: lineItemsData,
        variantsMapping,
        variantDataloading,
        isGuestOrPreviewMode,
      }),
    [lineItemsData, variantsMapping, variantDataloading, isGuestOrPreviewMode],
  );

  const updatedLineItemsData = useMemo(
    () => mappedLineItems?.map(updatedProductsWithVariant) || emptyArr,
    [mappedLineItems],
  );

  const updatedProductSetsData = useMemo(
    () => productSetsData?.map(updatedProductsWithVariant) || emptyArr,
    [productSetsData],
  );
  const updatedMedleySetsData = useMemo(
    () => medleySetsData?.map(updatedProductsWithVariant) || emptyArr,
    [medleySetsData],
  );

  const allAvailableProducts = [
    ...updatedLineItemsData,
    ...updatedProductSetsData,
    ...updatedMedleySetsData,
  ];

  const { loading: shopProductLoading } = usePopulateItems();
  const { loading: bulkProductLoading } = useBatchPopulateItems({
    type: ORDER_TYPES.organizer,
  });
  const { toggleConfirmationModal, renderConfirmationModal } = useConfirmationModal(
    removeItemsConfirmationSendToOthers,
  );

  const {
    emptyingCart,
    onProductChangeQuantity,
    removeProductFromCart,
    removeAllProductsFromCart,
  } = useManipulateCartProduct({
    emptyCartSuccessCb: toggleConfirmationModal,
  });

  const disabled = shopProductLoading || bulkProductLoading;

  const getUpdatedItem = (item) => {
    if (isGuestOrPreviewMode) return getCartItemDetailInGuestFlow(item);
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  useEffect(() => {
    if (!isEmpty(allAvailableProducts)) {
      let totalPricePerRecipient = 0;
      allAvailableProducts.forEach(({ price, quantity }) => {
        totalPricePerRecipient += price * quantity;
      });
      getTotalPricePerRecipient(totalPricePerRecipient);
    }
  }, [allAvailableProducts]);

  const renderProducts = (lineItems = emptyArr) =>
    lineItems.map((item) => {
      const updatedItem = getUpdatedItem(item);

      return (
        <LineItemCard
          key={`product-${item.id}-${item?.productId}-${item?.zoneId}`}
          intent={getCartProductType(item.type)}
          editable={editable}
          disabled={disabled}
          lineItem={updatedItem}
          removeProductFromCart={removeProductFromCart}
          onChangeQuantity={onProductChangeQuantity}
          getUpdatedItem={getUpdatedItem}
          zoneId={zoneId}
          orderCurrencyCode={orderCurrencyCode}
        />
      );
    });

  const viewAllClickHandler = () => setIsViewAllClicked(true);

  const renderGroupedCartProductListing = (groupedProducts, bothCategoriesPresent) => {
    return Object.keys(groupedProducts).map((key) => {
      const productList = groupedProducts[key];
      if (isEmpty(productList)) return;
      return (
        <dl className="" key={key}>
          {bothCategoriesPresent && (
            <dt className={styles.smallHeading}>
              {key === SHIPPED_TOGETHER ? "Ships From Stadium Warehouse" : "Ships Separately"}:
            </dt>
          )}
          {renderProducts(productList)}
        </dl>
      );
    });
  };

  const removeAllConfirmHandler = () => {
    withAnalytics(undefined, {
      track: true,
      action: ANALYTICS_EVENT_ACTION_CLEARED,
      eventProperties: { total_items: allAvailableProducts.length },
    })();
    setSendToOthersZoneWiseRecipient(
      sendToOthersZoneWiseRecipient.filter(
        ({ zoneId: recipientZoneId }) => recipientZoneId !== +zoneId,
      ),
    );
    removeAllProductsFromCart(orderId, zoneId);
  };

  const renderRemoveAllBtn = () => (
    <>
      <div className={`${styles.colAutoLink} ${styles.removeLinkCol}`}>
        <InternalButton
          className={styles.removeLink}
          onClick={toggleConfirmationModal}
          styleType="link"
        >
          REMOVE ALL ITEMS
        </InternalButton>
      </div>
      {renderConfirmationModal({
        loading: emptyingCart,
        onConfirmHandler: removeAllConfirmHandler,
      })}
    </>
  );

  const renderCartProductListing = () => {
    const isViewAllNotRequired = isProductFromSingleCountry || isMobileDevice;
    const orderedAllAvailableProducts = orderBy(allAvailableProducts, SHIPPED_SEPARATELY);
    const slicedProduct = orderedAllAvailableProducts.slice(0, VIEW_ALL_THRESHOLD);
    const { groupedProducts, bothCategoriesPresent } = groupItemsByShippedSeparately(
      isViewAllClicked || isViewAllNotRequired ? allAvailableProducts : slicedProduct,
    );
    return (
      <>
        {renderGroupedCartProductListing(groupedProducts, bothCategoriesPresent)}
        <div className={styles.btnGroupView}>
          {allAvailableProducts.length > VIEW_ALL_THRESHOLD &&
            !isViewAllClicked &&
            !isViewAllNotRequired && (
              <InternalButton
                styleType="link"
                onClick={viewAllClickHandler}
                className={styles.viewBtn}
              >
                View all
              </InternalButton>
            )}
          {renderRemoveAllBtn()}
        </div>
      </>
    );
  };

  return <div>{renderCartProductListing()}</div>;
};

export default CartProductItems;
